<script lang="ts" setup>
interface Props {
	prominent?: boolean;
}

interface SubscriptionCheckData {
	hasActiveSubscription: boolean;
	status: string | null;
	plan?: string;
	hiddenReportsCount: number;
}

const props = withDefaults(defineProps<Props>(), {
	prominent: false
});

const { loggedIn } = useUserSession();
const { data: rawData, status, refresh } = await useLazyFetch('/api/data/website/subscription-status', {
	key: 'subscription-status',
	immediate: loggedIn.value
});
const activeWebsite = useState('activeWebsite');
const route = useRoute();

const data = computed(() => {
	return rawData.value as SubscriptionCheckData | null;
});

const showNotification = computed(() => {
	const isReportsPage = route.path?.startsWith('/report');
	const shouldHideOnReports = !props.prominent && isReportsPage;

	return activeWebsite.value &&
		status.value !== 'pending' &&
		data.value &&
		!data.value.hasActiveSubscription &&
		!shouldHideOnReports;
});

const notificationMessage = computed(() => {
	if (!data.value?.status) {
		return 'Upgrade to start collecting reports';
	}

	switch (data.value.status) {
		case 'past_due':
			return 'Payment failed - update payment method';
		case 'paused':
			return 'Subscription paused - reports hidden';
		case 'canceled':
			return 'Subscription canceled - reports hidden';
		default:
			return 'Subscription inactive - reports hidden';
	}
});

const buttonLabel = computed(() => {
	if (!data.value?.status) {
		return 'Choose Plan';
	}
	return data.value.status === 'past_due' ? 'Update Payment' : 'Manage Subscription';
});

const buttonColor = computed(() => {
	if (!data.value?.status) {
		return 'primary';
	}
	return data.value.status === 'past_due' ? 'error' : 'warning';
});

watch(activeWebsite, () => {
	if (loggedIn.value) refresh();
});
onMounted(() => {
	if (loggedIn.value) refresh();
});
</script>

<template>
	<div v-if="showNotification" :class="[
		'border-b backdrop-blur-sm shadow-sm',
		prominent
			? 'bg-warning/15 dark:bg-warning/20 border-warning/25 dark:border-warning/35'
			: 'bg-warning/8 dark:bg-warning/12 border-warning/15 dark:border-warning/25'
	]">
		<div :class="[
			'px-4 flex items-center justify-between',
			prominent ? 'py-4' : 'py-2'
		]">
			<div class="flex items-center space-x-3 min-w-0 flex-1">
				<UIcon name="i-lucide-alert-triangle" :class="[
					'flex-shrink-0 text-warning',
					prominent ? 'w-5 h-5' : 'w-4 h-4'
				]" />
				<div class="flex-1 min-w-0">
					<p :class="[
						'font-medium text-highlighted',
						prominent ? 'text-sm' : 'text-xs'
					]">
						{{ notificationMessage }}
					</p>
					<p v-if="prominent" class="text-xs text-muted mt-1">
						<template v-if="data?.hiddenReportsCount && data.hiddenReportsCount > 0">
							{{ data.hiddenReportsCount }} report{{ data.hiddenReportsCount === 1 ? '' : 's' }}
							hidden until you have an active subscription.
						</template>
						<template v-else>
							New reports are hidden until you have an active subscription.
						</template>
					</p>
				</div>
			</div>
			<div class="flex items-center space-x-2 flex-shrink-0">
				<UButton to="/profile/website/subscription" :label="buttonLabel" :size="prominent ? 'sm' : 'xs'"
					:color="buttonColor" variant="solid" />
			</div>
		</div>
	</div>
</template>
